import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { APP_VERSION } from 'app/utils/appVersion';
import { subscribeBroadcast } from 'app/utils/broadcast';
import { readSession, writeSession } from 'app/utils/safeSessionStorage';
import { BROADCAST_EVENTS, SW_MESSAGES } from 'constants/broadcastEvents';
import { RELEASES_KEY, parseReleaseRecords } from './workerCache';

const APP_WORKER_URL = '/sw.js';
const CHECK_INTERVAL_MS = 5 * 60_000;
// Visibility events can fire in bursts; cap the request rate.
const MIN_CHECK_GAP_MS = 30_000;
const SWITCH_KEY_PREFIX = 'release-switch:';
// antd mounts a toast a few renders after open(), so one fired just before navigate() isn't in the DOM at commit.
const SWITCH_SETTLE_MS = 250;
const FEEDBACK_TOAST_SELECTOR = '.ant-message-notice, .ant-notification-notice';

type NavigatorWithConnection = Navigator & {
  connection?: { saveData?: boolean };
};

// A release the worker has fully cached that this tab does not run yet.
let pendingRelease: string | null = null;

const switchHolds = new Set<() => boolean>();

// Code with unsent in-memory work holds the switch back, because the reload would drop that work.
export const holdReleaseSwitch = (isHeld: () => boolean) => {
  switchHolds.add(isHeld);
  return () => {
    switchHolds.delete(isHeld);
  };
};

// A reload would wipe feedback the user is still reading; a later page change switches.
holdReleaseSwitch(
  () => document.querySelector(FEEDBACK_TOAST_SELECTOR) !== null
);

// Firefox throws a SecurityError from this getter when site storage is blocked.
const serviceWorkerContainer = (): ServiceWorkerContainer | null => {
  try {
    return 'serviceWorker' in navigator ? navigator.serviceWorker : null;
  } catch {
    return null;
  }
};

const isAppWorkerScript = (worker: ServiceWorker | null | undefined) =>
  worker != null && new URL(worker.scriptURL).pathname === APP_WORKER_URL;

const whenLoaded = () =>
  new Promise<void>((resolve) => {
    if (document.readyState === 'complete') resolve();
    else window.addEventListener('load', () => resolve(), { once: true });
  });

// ProtectedShell only, so one-time public visitors (signers) never download the whole app.
export const registerAppWorker = async () => {
  if (!import.meta.env.PROD) return;
  if (import.meta.env.VITE_SERVICE_WORKER === 'kill') return;
  const container = serviceWorkerContainer();
  if (!container) return;
  // The precache is the whole app; don't spend a data-saver user's allowance on it.
  if ((navigator as NavigatorWithConnection).connection?.saveData) return;
  await whenLoaded();
  try {
    const registration = await container.getRegistration('/');
    const newest =
      registration?.installing ?? registration?.waiting ?? registration?.active;
    // Registers only when '/' holds nothing or another script, e.g. FCM's from an older bundle.
    if (isAppWorkerScript(newest)) return;
    const registered = await container.register(APP_WORKER_URL, {
      scope: '/',
    });
    // A same-URL register() can resolve to the kill worker's leftover registration; force an update check.
    await registered.update().catch(() => {});
  } catch {
    // The stale-chunk self-heal still covers this tab.
  }
};

const listenForReadyRelease = () =>
  subscribeBroadcast((event) => {
    if (
      event.type === BROADCAST_EVENTS.APP_RELEASE_READY &&
      event.release !== APP_VERSION
    )
      pendingRelease = event.release;
  });

// For a tab that missed the broadcast: it booted from a cached old index.html, or was frozen.
const adoptSupersedingRelease = async () => {
  try {
    if (!APP_VERSION) return;
    const records = parseReleaseRecords(
      await (await caches.match(RELEASES_KEY))?.json()
    );
    const own = records?.find((record) => record.release === APP_VERSION);
    const serving = records?.find((record) => !record.superseded);
    if (own?.superseded && serving) pendingRelease = serving.release;
  } catch {
    // No Cache Storage or an unreadable record; the broadcast still reaches this tab.
  }
};

// Only the visible tab checks; the worker tells every tab. Not on 'focus': file pickers fire it.
const startUpdateChecks = () => {
  // Only a production build serves sw.js.
  if (!import.meta.env.PROD) return;
  void adoptSupersedingRelease();
  let intervalId: number | undefined;
  let lastCheckedAt = 0;

  const check = () => {
    if (Date.now() - lastCheckedAt < MIN_CHECK_GAP_MS) return;
    lastCheckedAt = Date.now();
    // A changed sw.js installs in the background and announces itself once fully cached.
    void serviceWorkerContainer()
      ?.getRegistration('/')
      .then((registration) => registration?.update())
      .catch(() => {});
  };

  const onVisibilityChange = () => {
    if (document.visibilityState !== 'visible') {
      window.clearInterval(intervalId);
      intervalId = undefined;
      return;
    }
    intervalId ??= window.setInterval(check, CHECK_INTERVAL_MS);
    check();
    void adoptSupersedingRelease();
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  if (document.visibilityState === 'visible')
    intervalId = window.setInterval(check, CHECK_INTERVAL_MS);

  return () => {
    window.clearInterval(intervalId);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };
};

// The worker keeps an old release's files only while some open tab reports running it.
const reportTabRelease = () => {
  if (!import.meta.env.PROD || !APP_VERSION) return;
  const container = serviceWorkerContainer();
  if (!container) return;
  // The active worker, not the controller: a tab left uncontrolled by Shift+Reload must still report.
  const report = async () => {
    try {
      const worker = (await container.getRegistration('/'))?.active;
      if (isAppWorkerScript(worker))
        worker?.postMessage({
          type: SW_MESSAGES.TAB_RELEASE,
          release: APP_VERSION,
        });
    } catch {
      // The worker must never break the app.
    }
  };
  void report();
  // A worker that just claimed this tab has not heard from it yet.
  container.addEventListener('controllerchange', report);
  // clients.matchAll() skips bfcache pages, so the worker may have dropped this release meanwhile.
  const onPageShow = (event: PageTransitionEvent) => {
    if (event.persisted) report();
  };
  window.addEventListener('pageshow', onPageShow);
  return () => {
    container.removeEventListener('controllerchange', report);
    window.removeEventListener('pageshow', onPageShow);
  };
};

// Answers "which build is this user on?" during a screen share or in DevTools.
const announceVersion = () => {
  if (APP_VERSION) console.info(`MaxHome ${APP_VERSION}`);
};

const noop = () => {};

// App starts this above both shells, so one failing piece must not take the app down.
const safely = (start: () => void | (() => void)): (() => void) => {
  try {
    return start() || noop;
  } catch {
    return noop;
  }
};

export const startReleaseSync = (): (() => void) => {
  const stops = [
    listenForReadyRelease,
    startUpdateChecks,
    reportTabRelease,
    announceVersion,
  ].map(safely);
  return () => stops.forEach(safely);
};

const normalizePath = (pathname: string) => pathname.replace(/\/+$/, '') || '/';

// A signer's flow is one task across several paths.
const isSigningPath = (pathname: string) => pathname.startsWith('/signature/');

// The marker is the loop guard, so blocked storage means no switch at all.
const claimSwitch = (release: string) =>
  readSession(SWITCH_KEY_PREFIX + release) === null &&
  writeSession(SWITCH_KEY_PREFIX + release, '1');

// The router has already written the URL and history.state, so the reload lands on the destination.
export const useReleaseSwitch = () => {
  const { pathname } = useLocation();
  const previousPathname = useRef(pathname);

  // Not a layout effect: the page just left must drop its beforeunload guard first, or the reload re-prompts.
  useEffect(() => {
    const from = previousPathname.current;
    previousPathname.current = pathname;
    const release = pendingRelease;
    if (!release) return;
    if (normalizePath(from) === normalizePath(pathname)) return;
    if (isSigningPath(from) || isSigningPath(pathname)) return;
    const timer = window.setTimeout(() => {
      try {
        // A newer navigation is waiting on a lazy route; its commit switches once the left page drops its unload guard.
        if (normalizePath(window.location.pathname) !== normalizePath(pathname))
          return;
        // Offline, the reload lands on the browser's error page; the next page change after reconnecting switches.
        if (!navigator.onLine) return;
        if ([...switchHolds].some((isHeld) => isHeld())) return;
        if (!claimSwitch(release)) return;
        window.location.reload();
      } catch {
        // The tab stays on its current build, which still works.
      }
    }, SWITCH_SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [pathname]);
};
